import { isNil } from "lodash";
import moment, { tz } from "moment-timezone";
import { t } from "ttag";
import * as Yup from "yup";
import { countries } from "../countryList";
import { stateList } from "../stateList";
import { debugPackage, trialPackage } from "../temporaryPackageField";

// Setup form data: initial values, validation, options and API payload.

// Site fields matching Edit Site. Built per open so Debug/Trial defaults start from now.
export const getEmptySiteSetupFields = () => ({
  name: "",
  address: "",
  city: "",
  postalCode: "",
  country: "",
  state: "",
  timezone: "",
  description: "",
  category: "",
  onBoardingStatus: "",
  phase: "",
  hvacAdvancedOperations: false,
  predictiveMaintenance: false,
  isEnforcePackage: false,
  debugPackageEnabled: false,
  debugPackageExpiresAt: debugPackage.initialExpiresAt(null),
  trialPackageEnabled: false,
  trialPackageExpiresAt: trialPackage.initialExpiresAt(null),
  sellingEntity: "",
  payingCustomerName: "",
  invoiceNum: "",
  PONum: "",
  sfOrderNum: "",
  salesAccountEmail: "",
  contractedIDUs: "",
  activePeriodStart: null,
  activePeriodEnd: null
});

const activePeriodRequired = t`Required for the expiration notice`;

export const siteSetupFieldsShape = {
  name: Yup.string().trim().max(50, t`Max 50 characters`).required("Required"),
  city: Yup.string().required("Required"),
  address: Yup.string().required("Required"),
  postalCode: Yup.string().required("Required"),
  country: Yup.string().required("Required"),
  state: Yup.string(),
  timezone: Yup.string().required("Required"),
  salesAccountEmail: Yup.string().trim().email(t`Invalid email`),
  // The expiration notice skips sites without both dates, so they're required when it's on
  activePeriodStart: Yup.date().nullable().when("isEnforcePackage", {
    is: true,
    then: Yup.date().nullable().required(activePeriodRequired)
  }),
  activePeriodEnd: Yup.date().nullable().when(
    ["activePeriodStart", "isEnforcePackage"],
    (...args: any[]) => {
      const [start, enforce, schema] = args;
      let rule = schema;
      if (start) {
        rule = rule.min(Yup.ref("activePeriodStart"), t`Must be after the start date`);
      }
      if (enforce) {
        rule = rule.required(activePeriodRequired);
      }
      return rule;
    }
  )
};

export const countryOptions: any = countries.map((country: any) => ({
  value: country.code,
  label: country.name
}));

// Without the "No Option" entry: the field can simply be cleared
export const stateOptions: any = stateList
  .filter((state: any) => state.value)
  .map((state: any) => ({ value: state.value, label: state.label }));

export const timezoneOptions: any = tz.names().map((timezone: any) => ({
  value: timezone,
  label: `${timezone}  ${tz(timezone).format("Z")}`
}));

// The state list only holds US states
export const countryWithStates = "US";

// For the searchable dropdowns, whose options are { value, label }
export const getOptionLabel = (option: any) => option.label;
export const isSameOption = (option: any, value: any) => option.value === value.value;

// Options for the short enum lists that come from the types store
export const enumOptions = (enumMap: any = {}): any =>
  Object.entries(enumMap).map(([label, value]) => ({ value, label }));

const textFieldKeys: any[] = [
  "name", "address", "city", "postalCode", "state", "description",
  "payingCustomerName", "invoiceNum", "PONum", "sfOrderNum", "salesAccountEmail"
];
const numberFieldKeys: any[] = ["category", "onBoardingStatus", "phase", "sellingEntity", "contractedIDUs"];
const dateFieldKeys: any[] = ["activePeriodStart", "activePeriodEnd"];

// The "site" part of the setup API payload; leaves out anything not filled in
export const toSiteSetupPayload = (fields: any, initialFields: any) => {
  let payload: any = {
    country: fields.country,
    timezone: fields.timezone,
    hvacAdvancedOperations: fields.hvacAdvancedOperations,
    predictiveMaintenance: fields.predictiveMaintenance,
    // hvacControl is worked out by the backend from the two packages
    isEnforcePackage: fields.isEnforcePackage
  };

  textFieldKeys.forEach((key) => {
    const value = fields[key].trim();
    if (value) {
      payload[key] = value;
    }
  });
  numberFieldKeys.forEach((key) => {
    if (fields[key] !== "" && !isNil(fields[key])) {
      payload[key] = +fields[key];
    }
  });
  dateFieldKeys.forEach((key) => {
    if (fields[key]) {
      payload[key] = moment(fields[key]).valueOf();
    }
  });

  // Sends { enabled: true } for the backend default, or the picked date when it was changed
  payload = debugPackage.apply(
    payload, fields.debugPackageEnabled, fields.debugPackageExpiresAt, initialFields.debugPackageExpiresAt, false
  );
  payload = trialPackage.apply(
    payload, fields.trialPackageEnabled, fields.trialPackageExpiresAt, initialFields.trialPackageExpiresAt, false
  );

  return payload;
};
